import { useEditorSelector, EditorContext, useEditorMode } from 'app/state/contexts/EditorContext';
import { useCoverageScope } from 'app/components/editor/coverage/CoverageScopeContext';
import { useMemo, useState, useCallback, useRef, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Transforms } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import { StyledDataSheet } from 'app/components';
import { SeletedTextWrapper } from './Paragraph';
import { AddComment } from 'app/components/editor/sidebar/comments/AddComment';
import { AddTag } from 'app/components/editor/sidebar/tags/AddTag';
import { AiSuggestTags } from 'app/components/editor/sidebar/tags/AiSuggestTags';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { TagContext } from 'app/state/contexts/TagContext';
import { useBlockTagColors, blockTagHighlightStyle } from 'app/slate/extensions/tag/utils/useBlockTagColors';
import { isNumericText } from 'app/utils/tableCells';
import { BlockTagBadge } from 'app/slate/extensions/tag/components/BlockTagBadge';

const EMPTY_SELECTION = { start: {}, end: {} };

// Helper to get individual cells from selection
const getSelectedCells = (data, selection) => {
  if (selection?.start?.i === undefined || !data) return [];

  const { start, end } = selection;
  const startRow = Math.min(start.i, end.i);
  const endRow = Math.max(start.i, end.i);
  const startCol = Math.min(start.j, end.j);
  const endCol = Math.max(start.j, end.j);

  const cells = [];
  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      const cell = data[row]?.[col];
      if (cell) {
        cells.push({
          row,
          col,
          value: cell.value,
          isNumeric: isNumericText(cell.value?.toString() ?? ''),
        });
      }
    }
  }
  return cells;
};

export const TaggableTable = (props) => {
  const { element, attributes, children, withComments, withTags } = props;
  const references = useEditorSelector((editor) => editor.references);
  const pDocument = useEditorSelector((editor) => editor.pDocument);
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const containerRef = useRef(null);
  const { shadowHost } = useContext(EditorContext) || {};
  const slateEditor = useSlate();
  const { taggingSubMode, isTaggingMode } = useEditorMode();
  const { scopeUuids, missingUuids, anchorsVisible } = useCoverageScope();
  const selectTableForCoverage = useCallback(() => {
    if (taggingSubMode !== 'mark-coverage') return;
    try {
      const path = ReactEditor.findPath(slateEditor, element);
      Transforms.select(slateEditor, path);
    } catch (err) {
      console.error('Path is not resolvable', err);
    }
  }, [taggingSubMode, slateEditor, element]);
  const { setIsSidebarOpen } = useContext(SidebarContext) || {};
  const { highlightedOption, tagDataMap, setClickedTagIds, setTableTagSelection, clearTableTagSelection, tableSelection, calcPickMode, termHighlightTagIds } =
    useContext(TagContext) || {};

  // Clear selection when clicking outside the table but inside the document
  useEffect(() => {
    if (!shadowHost?.shadowRoot) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setSelection(EMPTY_SELECTION);
      }
    };

    shadowHost.shadowRoot.addEventListener('mousedown', handleClickOutside);
    return () => {
      shadowHost.shadowRoot.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shadowHost]);

  const content = useMemo(() => {
    return references?.find((r) => r.superId === element.referenceDataSuperId);
  }, [references, element]);

  const handleMouseDown = useCallback(
    (e) => {
      e.stopPropagation();
      // In coverage mode the capture-phase handler selects this table so the
      // marker controls can target it; don't clear that selection here.
      if (taggingSubMode === 'mark-coverage') return;
      const shadowRoot = shadowHost?.shadowRoot;
      const domSelection = shadowRoot ? (shadowRoot.getSelection ? shadowRoot : shadowRoot.ownerDocument).getSelection() : window.getSelection?.();
      domSelection?.removeAllRanges?.();
      if (slateEditor.selection) {
        Transforms.deselect(slateEditor);
      }
    },
    [shadowHost, slateEditor, taggingSubMode]
  );

  // Get cells that have been tagged by reading tags from cell.tags in selectionContent
  const taggedCells = useMemo(() => {
    if (!content?.selectionContent) return [];
    const cells = [];
    content.selectionContent.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (cell.tags && cell.tags.length > 0) {
          // Each cell can have multiple tags, add an entry for each
          cell.tags.forEach((tagId) => {
            cells.push({
              cellRow: rowIndex,
              cellCol: colIndex,
              tagId: tagId,
            });
          });
        }
      });
    });
    return cells;
  }, [content?.selectionContent]);

  // Handle click on a tagged cell — open sidebar with tag info. In calc
  // pick mode (GABY-1975) the click instead toggles the cell's concept as a
  // term of the sum being edited; the sum's own concept is locked.
  const handleTagClick = useCallback(
    (tagIds) => {
      if (!tagIds || tagIds.length === 0) return;
      // Coverage mode: tags are read-only, clicking a tagged cell does nothing.
      if (taggingSubMode === 'mark-coverage') return;
      if (calcPickMode) {
        const conceptId = tagDataMap?.[tagIds[0]]?.conceptId;
        if (!conceptId || conceptId === calcPickMode.parentConceptId) return;
        calcPickMode.onToggleTerm?.(conceptId, tagIds[0]);
        return;
      }
      if (setClickedTagIds) {
        setClickedTagIds(tagIds);
      }
      if (setIsSidebarOpen) {
        setIsSidebarOpen(8);
      }
    },
    [setClickedTagIds, setIsSidebarOpen, calcPickMode, tagDataMap, taggingSubMode]
  );

  // Get selected cells data when selection changes
  const selectedCells = useMemo(() => {
    if (!content?.selectionContent || selection.start?.i === undefined) return [];
    return getSelectedCells(content.selectionContent, selection);
  }, [content, selection]);

  const ownsTableSelectionRef = useRef(false);
  useEffect(() => {
    if (!setTableTagSelection || !clearTableTagSelection) return;
    if (selectedCells.length > 0) {
      ownsTableSelectionRef.current = true;
      setTableTagSelection({
        element,
        referenceDataSuperId: element.referenceDataSuperId,
        cells: selectedCells,
        onTagsCreated: () => setSelection(EMPTY_SELECTION),
      });
    } else if (ownsTableSelectionRef.current) {
      ownsTableSelectionRef.current = false;
      clearTableTagSelection();
    }
  }, [selectedCells, element, setTableTagSelection, clearTableTagSelection]);

  const blockTagBindings = useMemo(() => (Array.isArray(element.tags) ? element.tags : []), [element.tags]);
  const hasBlockTag = blockTagBindings.length > 0;
  const blockTagColors = useBlockTagColors(hasBlockTag ? blockTagBindings : undefined);

  const isEmbedded = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('embedd') === 'true';
  }, []);
  const documentPermissions = useSelector((state) => state.document?.documentPermissions) || [];
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');

  if (!content) {
    return (
      <div {...attributes}>
        <div style={{ userSelect: 'none' }} contentEditable={false}>
          Loading data...
        </div>
        {children}
      </div>
    );
  }

  const hasSelection = selection?.start?.i !== undefined;
  const metadata = {
    'data-element-super-id': element.referenceDataSuperId,
    'data-element-uuid': element.uuid,
    'data-document-revision-id': content.revisionId,
    'data-element-revision-id': `${element.referenceDataSuperId}_${content.revisionId}`,
  };
  const isTableSelected = !!tableSelection?.element && tableSelection.element.uuid === element.uuid;
  const blockTagClass = isTableSelected ? ' taggable-table-wrapper--block-tagged' : '';
  const showBlockTagVisual = hasBlockTag && !isEmbedded && isTaggingMode && blockTagColors && !blockTagColors.isHidden;
  const blockTagStyle = {
    ...(showBlockTagVisual ? blockTagHighlightStyle(blockTagColors) : {}),
    ...(isTableSelected && blockTagColors ? { outlineColor: blockTagColors.indicator } : {}),
  };
  const coverageClass =
    anchorsVisible && element.coverage && element.coverage.rule && element.coverage.rule.kind
      ? ` coverage-anchor coverage-anchor--${element.coverage.rule.kind}`
      : '';
  let scopeClass = '';
  if (element.uuid) {
    if (missingUuids.has(element.uuid)) scopeClass = ' coverage-missing';
    else if (scopeUuids.has(element.uuid)) scopeClass = ' coverage-scope';
  }
  return (
    <div
      {...metadata}
      className={`taggable-table-wrapper${coverageClass}${scopeClass}${blockTagClass}`}
      style={blockTagStyle ?? undefined}
      onMouseDownCapture={selectTableForCoverage}
      onClick={(e) => {
        if (!hasBlockTag || taggingSubMode === 'mark-coverage') return;
        const cell = e.target.closest && e.target.closest('td, th');
        if (cell) return;
        setClickedTagIds?.(blockTagBindings.map((b) => b.tagId));
        setIsSidebarOpen?.(8);
      }}
    >
      {hasBlockTag && !isEmbedded && isTaggingMode && (
        <BlockTagBadge
          bindings={blockTagBindings}
          tooltipKey="tag.block.table.tagged"
          canEdit={canEditTags}
          onContinueClick={(firstTagId) => {
            setClickedTagIds?.([firstTagId]);
            setIsSidebarOpen?.(8);
          }}
          onSelectTag={(tagId) => {
            setClickedTagIds?.([tagId]);
            setIsSidebarOpen?.(8);
          }}
        />
      )}
      <div ref={containerRef} contentEditable={false} onMouseDown={handleMouseDown} className="taggable-table-container">
        <StyledDataSheet
          data={content.selectionContent}
          selected={selection}
          onSelect={setSelection}
          className="taggable-table"
          taggedCells={taggedCells}
          onTagClick={handleTagClick}
          withTags={withTags}
          highlightedOption={highlightedOption}
          tagDataMap={tagDataMap}
          calcPickMode={calcPickMode}
          termHighlightTagIds={termHighlightTagIds}
        />
        <SeletedTextWrapper popperRef={containerRef.current} show={hasSelection}>
          <>
            {withComments && containerRef.current ? <AddComment element={element} /> : null}
            {withTags && containerRef.current ? (
              <AddTag
                element={element}
                selectedCells={selectedCells}
                referenceDataSuperId={element.referenceDataSuperId}
                onTagsCreated={() => setSelection(EMPTY_SELECTION)}
              />
            ) : null}
            {withTags && containerRef.current ? (
              <AiSuggestTags
                element={element}
                referenceDataSuperId={element.referenceDataSuperId}
                documentSuperId={pDocument?.superId}
                selectedCells={selectedCells}
              />
            ) : null}
          </>
        </SeletedTextWrapper>
      </div>
      {children}
    </div>
  );
};
